import React from 'react';
import { Link } from 'wouter';
import type { SeoTextSectionConfig } from '@/lib/sections/types';
import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { isUnavailableBouquetCreatorPath } from '@/lib/storeFeatures';
import './seo-text-section.css';

/* ─── Design tokens ─────────────────────────────────────────── */
const BG   = '#ffffff';
const CARD = '#ffffff';
const GOLD = '#b83f74';
const ROSE = '#b44f3f';   /* icon / highlight / link colour */
const DARK = '#2c1a0e';
const BODY = '#5e4535';
const BORDER = 'rgba(0,0,0,0.07)';

/* ─── Helpers ───────────────────────────────────────────────── */
type InlineLink = { word: string; href: string };

/**
 * Renders body text where only words that have an explicit href become
 * rose-coloured clickable links. All other text stays at the normal body colour.
 */
function LinkedText({ text, linked, fieldPrefix, section }: { text: string; linked: InlineLink[]; fieldPrefix: string; section: string }) {
  if (!linked.length) return <CmsEditorialText id={`${fieldPrefix}.0`} value={text} label="Opis" section={section} multiline />;
  const escaped = linked.map(l => l.word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(pattern);
  return (
    <>
      {parts.map((part, i) => {
        const match = linked.find(l => l.word.toLowerCase() === part.toLowerCase());
        if (match && isUnavailableBouquetCreatorPath(match.href)) {
          return (
            <React.Fragment key={i}>
              <CmsEditorialText id={`${fieldPrefix}.${i}`} value={part} label={`Tekst odnośnika ${i + 1}`} section={section} />
            </React.Fragment>
          );
        }
        return match ? (
          <Link
            key={i}
            href={match.href}
            style={{ color: ROSE, fontWeight: 500, textDecoration: 'none' }}
          >
            <CmsEditorialText id={`${fieldPrefix}.${i}`} value={part} label={`Tekst odnośnika ${i + 1}`} section={section} />
          </Link>
        ) : part.trim() ? <CmsEditorialText key={i} id={`${fieldPrefix}.${i}`} value={part} label={`Fragment opisu ${i + 1}`} section={section} multiline /> : part;
      })}
    </>
  );
}

function CardLink({ href, children }: { href: string; children: React.ReactNode }) {
  if (isUnavailableBouquetCreatorPath(href)) {
    return (
      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, fontWeight: 500, color: BODY }}>
        {children} <span style={{ whiteSpace: 'nowrap' }}>— Wkrótce</span>
      </span>
    );
  }

  return (
    <Link
      href={href}
      style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 12,
        fontWeight: 500,
        color: ROSE,
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        whiteSpace: 'nowrap' as const,
      }}
    >
      {children} <span style={{ fontSize: 13 }}>›</span>
    </Link>
  );
}

/* ─── Icons ─────────────────────────────────────────────────── */
function IconStore() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <rect x="4" y="16" width="28" height="16" rx="1.5" stroke={ROSE} strokeWidth="1.6"/>
      <path d="M4 16 L8 6 h20 L32 16" stroke={ROSE} strokeWidth="1.6" strokeLinejoin="round"/>
      <path d="M14 32 V22 a4 4 0 0 1 8 0 V32" stroke={ROSE} strokeWidth="1.5"/>
      <path d="M4 16 h28" stroke={ROSE} strokeWidth="1.6"/>
      <path d="M2 16 C2 20 6 22 6 18 C6 14 10 12 10 16 C10 20 14 22 14 18 C14 14 18 12 18 16 C18 20 22 22 22 18 C22 14 26 12 26 16 C26 20 30 22 30 18 C30 14 34 16 34 16" stroke={ROSE} strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}

function IconDelivery() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <rect x="2" y="11" width="22" height="16" rx="1.5" stroke={ROSE} strokeWidth="1.6"/>
      <path d="M24 15 L30 15 L34 20 L34 27 L24 27 Z" stroke={ROSE} strokeWidth="1.6" strokeLinejoin="round"/>
      <circle cx="8" cy="27" r="3" stroke={ROSE} strokeWidth="1.5"/>
      <circle cx="28" cy="27" r="3" stroke={ROSE} strokeWidth="1.5"/>
      <line x1="11" y1="27" x2="25" y2="27" stroke={ROSE} strokeWidth="1.4"/>
      <line x1="10" y1="17" x2="18" y2="17" stroke={ROSE} strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}

function IconBouquet() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M18 8 C18 5 15 3 12 5 C9 7 10 11 13 11 C10 11 7 13 8 17 C9 21 13 21 15 18 C15 21 17 24 18 26 C19 24 21 21 21 18 C23 21 27 21 28 17 C29 13 26 11 23 11 C26 11 27 7 24 5 C21 3 18 5 18 8Z" stroke={ROSE} strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M14 26 L15 34 M18 26 L18 34 M22 26 L21 34" stroke={ROSE} strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M13 34 Q18 31 23 34" stroke={ROSE} strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function IconRose() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M18 6 C18 6 22 8 22 12 C22 16 18 17 18 17 C18 17 14 16 14 12 C14 8 18 6 18 6Z" stroke={ROSE} strokeWidth="1.5"/>
      <path d="M18 17 C18 17 24 14 27 18 C30 22 26 26 22 25 C20 24 18 22 18 22" stroke={ROSE} strokeWidth="1.5"/>
      <path d="M18 17 C18 17 12 14 9 18 C6 22 10 26 14 25 C16 24 18 22 18 22" stroke={ROSE} strokeWidth="1.5"/>
      <path d="M18 22 C18 24 17 27 16 30 L20 30 C19 27 18 24 18 22Z" stroke={ROSE} strokeWidth="1.3"/>
      <path d="M13 28 C11 26 9 22 10 19" stroke={ROSE} strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

function IconFlowerBox() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <rect x="5" y="18" width="26" height="14" rx="1.5" stroke={ROSE} strokeWidth="1.6"/>
      <rect x="3" y="14" width="30" height="5" rx="1" stroke={ROSE} strokeWidth="1.5"/>
      <line x1="18" y1="14" x2="18" y2="32" stroke={ROSE} strokeWidth="1.4"/>
      <path d="M18 14 C16 12 12 11 11 13 C10 15 13 17 18 14Z" stroke={ROSE} strokeWidth="1.3" fill="none"/>
      <path d="M18 14 C20 12 24 11 25 13 C26 15 23 17 18 14Z" stroke={ROSE} strokeWidth="1.3" fill="none"/>
      <circle cx="11" cy="8" r="3" stroke={ROSE} strokeWidth="1.3"/>
      <circle cx="18" cy="6" r="3" stroke={ROSE} strokeWidth="1.3"/>
      <circle cx="25" cy="8" r="3" stroke={ROSE} strokeWidth="1.3"/>
    </svg>
  );
}

function IconForLoved() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <circle cx="18" cy="10" r="5" stroke={ROSE} strokeWidth="1.6"/>
      <path d="M8 32 C8 25 28 25 28 32" stroke={ROSE} strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M18 18 C18 18 14 20 14 23 C14 25.2 15.8 27 18 27 C20.2 27 22 25.2 22 23 C22 20 18 18 18 18Z" stroke={ROSE} strokeWidth="1.4"/>
    </svg>
  );
}

function IconWreath() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <circle cx="18" cy="18" r="12" stroke={ROSE} strokeWidth="1.6"/>
      <circle cx="18" cy="18" r="6" stroke={ROSE} strokeWidth="1.3"/>
      <path d="M18 6 C18 6 20 9 18 12 C16 9 18 6 18 6Z" fill={ROSE} opacity="0.7"/>
      <path d="M30 18 C30 18 27 20 24 18 C27 16 30 18 30 18Z" fill={ROSE} opacity="0.7"/>
      <path d="M18 30 C18 30 16 27 18 24 C20 27 18 30 18 30Z" fill={ROSE} opacity="0.7"/>
      <path d="M6 18 C6 18 9 16 12 18 C9 20 6 18 6 18Z" fill={ROSE} opacity="0.7"/>
      <path d="M25.5 10.5 C25.5 10.5 24 14 21 13 C22 10 25.5 10.5 25.5 10.5Z" fill={ROSE} opacity="0.6"/>
      <path d="M10.5 25.5 C10.5 25.5 12 22 15 23 C14 26 10.5 25.5 10.5 25.5Z" fill={ROSE} opacity="0.6"/>
    </svg>
  );
}

function IconOnline() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <rect x="4" y="7" width="28" height="18" rx="2" stroke={ROSE} strokeWidth="1.6"/>
      <line x1="12" y1="29" x2="24" y2="29" stroke={ROSE} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="18" y1="25" x2="18" y2="29" stroke={ROSE} strokeWidth="1.4"/>
      <path d="M13 16 L16 19 L23 13" stroke={ROSE} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ─── Icon map — resolves the JSON-serializable iconKey from config ── */
const ICON_MAP: Record<string, React.FC> = {
  store: IconStore,
  delivery: IconDelivery,
  bouquet: IconBouquet,
  rose: IconRose,
  flowerBox: IconFlowerBox,
  forLoved: IconForLoved,
  wreath: IconWreath,
  online: IconOnline,
};

function subscribeToMobileViewport(onChange: () => void) {
  const media = window.matchMedia('(max-width: 767px)');
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

const getMobileViewport = () => window.matchMedia('(max-width: 767px)').matches;
const getServerMobileViewport = () => false;

/* ─── Component ─────────────────────────────────────────────── */
export function SeoTextSection({ config }: { config: SeoTextSectionConfig }) {
  const { eyebrow, title: heading, tagsLabel, cards: CARDS, tags: TAGS } = config;
  const isMobile = React.useSyncExternalStore(subscribeToMobileViewport, getMobileViewport, getServerMobileViewport);
  const [expandedCards, setExpandedCards] = React.useState<Record<number, boolean>>({});

  return (
    <section aria-label="O Bukietowej" style={{ background: BG, paddingBottom: 0, position: 'relative', overflow: 'hidden' }}>

      {/* ── Header ── */}
      <div style={{
        maxWidth: 960,
        margin: '0 auto',
        padding: 'clamp(32px,4vw,48px) clamp(24px,5vw,48px)',
        textAlign: 'center',
      }}>
        {/* Eyebrow */}
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 24 }}
        >
          <span style={{ display: 'block', height: 1, width: 36, background: `linear-gradient(to right, transparent, ${GOLD})` }} />
          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.38em', textTransform: 'uppercase', color: GOLD }}>
            <CmsEditorialText id="home.seo.eyebrow" value={eyebrow} section="O Bukietowej" label="Nadtytuł" />
          </span>
          <span style={{ display: 'block', height: 1, width: 36, background: `linear-gradient(to left, transparent, ${GOLD})` }} />
        </div>

        {/* Heading */}
        <p
          style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(26px, 3.5vw, 40px)',
            fontWeight: 600,
            color: DARK,
            lineHeight: 1.25,
            letterSpacing: '-0.02em',
            margin: 0,
          }}
        >
          <CmsEditorialText id="home.seo.heading" value={heading} section="O Bukietowej" label="Nagłówek sekcji" />
        </p>
      </div>

      {/* ── Card grid ── */}
      <div style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: '0 clamp(16px, 3vw, 40px)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
        gap: 16,
        paddingBottom: 32,
      }}>
        {CARDS.map(({ iconKey, title, subheading, body, linked, links }, i) => {
          const Icon = ICON_MAP[iconKey] ?? IconStore;
          const expanded = Boolean(expandedCards[i]);
          const contentId = `home-seo-card-${i}-content`;
          const titleContent = <CmsEditorialText id={`home.seo.cards.${i}.title`} value={title} section={`O Bukietowej — karta ${i + 1}`} label="Tytuł" />;
          return (
          <div
            key={i}
            className="seo-text-card"
            data-expanded={expanded}
            style={{
              background: CARD,
              borderRadius: 12,
              border: `1px solid ${BORDER}`,
              padding: 'clamp(16px,2vw,24px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
            }}
          >
            {/* Icon + title */}
            <div className="seo-text-card-heading" style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <span className="hidden md:contents"><Icon /></span>
              <h2 style={{
                fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 'clamp(24px,2.2vw,30px)',
                fontWeight: 600,
                color: DARK,
                lineHeight: 1.3,
                margin: 0,
                flex: 1,
                whiteSpace: 'pre-line' as const,
              }}>
                {isMobile ? (
                  <button
                    type="button"
                    className="seo-text-card-toggle"
                    aria-expanded={expanded}
                    aria-controls={contentId}
                    onClick={() => setExpandedCards(current => ({ ...current, [i]: !current[i] }))}
                  >
                    <span>{titleContent}</span>
                    <span className="seo-text-card-indicator" aria-hidden="true">{expanded ? '−' : '+'}</span>
                  </button>
                ) : titleContent}
              </h2>
            </div>

            <div id={contentId} className="seo-text-card-content">
            {/* Body */}
            <div style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 'clamp(14px,1.1vw,15px)',
              lineHeight: 1.7,
              color: BODY,
              margin: '0 0 16px',
              flex: 1,
            }}>
              {body.split('\n\n').map((paragraph, paragraphIndex) => (
                <React.Fragment key={paragraphIndex}>
                  {paragraphIndex === 1 && subheading && (
                    <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 16, fontWeight: 600, color: DARK, lineHeight: 1.5, margin: '20px 0 10px' }}>
                      <CmsEditorialText id={`home.seo.cards.${i}.subheading`} value={subheading} section={`O Bukietowej — karta ${i + 1}`} label="Podtytuł" />
                    </h3>
                  )}
                  <p style={{ margin: paragraphIndex ? '16px 0 0' : 0 }}>
                    <LinkedText text={paragraph} linked={linked} fieldPrefix={`home.seo.cards.${i}.body${paragraphIndex ? `.paragraph${paragraphIndex}` : ''}`} section={`O Bukietowej — karta ${i + 1}`} />
                  </p>
                </React.Fragment>
              ))}
            </div>

            {/* Footer links */}
            {links.length > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 16, borderTop: `1px solid ${BORDER}`, paddingTop: 12, marginTop: 'auto', flexWrap: 'wrap' as const }}>
              {links.map(({ label, href }) => (
                <CardLink key={label} href={href}>{label}</CardLink>
              ))}
            </div>}
            </div>
          </div>
          );
        })}
      </div>

      {/* ── Tag chips ── */}
      {TAGS.length > 0 && <div style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: '16px clamp(16px,3vw,40px) clamp(40px,5vw,60px)',
      }}>
        {/* "Najczęściej wybierane" label */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          marginBottom: 16,
        }}>
          <span style={{ display: 'block', height: 1, flex: 1, background: `linear-gradient(to right, transparent, ${BORDER} 80%)` }} />
          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.36em', textTransform: 'uppercase', color: BODY, whiteSpace: 'nowrap' as const }}>
            {tagsLabel}
          </span>
          <span style={{ display: 'block', height: 1, flex: 1, background: `linear-gradient(to left, transparent, ${BORDER} 80%)` }} />
        </div>

        {/* Chips row */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap' as const,
          justifyContent: 'center',
          gap: '8px 10px',
        }}>
          {TAGS.map(({ label, href, icon }) => (
            isUnavailableBouquetCreatorPath(href) ? (
              <span
                key={label}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 999,
                  border: '1px solid rgba(0,0,0,0.13)', background: 'rgba(255,255,255,0.75)',
                  fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: BODY, whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>
                {label} — Wkrótce
              </span>
            ) : (
            <Link
              key={label}
              href={href}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 14px',
                borderRadius: 999,
                border: `1px solid rgba(0,0,0,0.13)`,
                background: 'rgba(255,255,255,0.75)',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 12.5,
                color: DARK,
                textDecoration: 'none',
                whiteSpace: 'nowrap' as const,
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.currentTarget.style.borderColor = ROSE;
                e.currentTarget.style.background = 'rgba(194,88,70,0.06)';
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.currentTarget.style.borderColor = 'rgba(0,0,0,0.13)';
                e.currentTarget.style.background = 'rgba(255,255,255,0.75)';
              }}
            >
              <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>
              {label}
            </Link>
            )
          ))}
        </div>
      </div>}


    </section>
  );
}
